import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { BookContext } from "../contexts/BookContext";
import { AnecdotesModalContext } from "../contexts/AnecdotesModalContext";
import { Locale } from "../types/models/Language";
import { ChapterStatusBadge } from "./Badges/ChapterStatusBadge";
import { ChapterTableActionsButton } from "./Buttons/ChapterTableActionsButton";
import { ProgressBar } from "./ProgressBar";

export const ChapterQuestionsTable: FC = () => {
  const { t } = useTranslation();
  const local = (localStorage.getItem("i18nextLng") || "en") as Locale;
  const bookContext = useContext(BookContext);
  const { openModal } = useContext(AnecdotesModalContext);

  const [renderKey, setRenderKey] = useState(300);

  useEffect(() => {
    setRenderKey((prevKey) => prevKey + 1);
  }, [bookContext.questions, bookContext.answers]);

  const getChapterPercentage = useCallback(
    (chapter_id: number): number => {
      const questions = bookContext.questions.filter(
        (question) => question.chapter_id === chapter_id
      );
      const questions_ids = questions.map((question) => question.id);
      const answers = bookContext.answers.filter((answer) =>
        questions_ids.includes(answer.question_id)
      );
      const completedAnswers = answers.filter((answer) => answer.completed);
      return questions.length > 0
        ? (completedAnswers.length / questions.length) * 100
        : 0;
    },
    [bookContext.questions, bookContext.answers]
  );

  // Utiliser useCallback pour optimiser les calculs de chapitres
  const calculateChapterData = useCallback(() => {
    return bookContext.chapters
      .sort((a, b) => a.number - b.number)
      .map((chapter) => {
        const percentage = getChapterPercentage(chapter.id);
        const questions = bookContext.questions.filter(
          (q) => q.chapter_id === chapter.id
        );

        const completed = bookContext.answers.filter(
          (answer) =>
            answer.completed &&
            questions.map((q) => q.id).includes(answer.question_id)
        ).length;

        const on = questions.length;

        return {
          chapter,
          percentage,
          completed,
          on,
        };
      });
  }, [
    bookContext.chapters,
    bookContext.questions,
    bookContext.answers,
    getChapterPercentage,
  ]);

  // Utiliser useMemo pour mémoriser les données des chapitres
  const chapterData = useMemo(
    () => calculateChapterData(),
    [calculateChapterData]
  );

  const handleOpenAnecdotesModal = (
    chapterNumber: number,
    chapterTitle: string
  ) => {
    // Find the generated chapter corresponding to this chapter
    const chapter = bookContext.chapters.find((c) => c.number === chapterNumber);
    const generatedChapter = bookContext.generated_chapters.find(
      (gc) => gc.chapter_id === chapter?.id
    );

    // Open modal with chapterNumber (always present) and generatedChapter.id (optional)
    openModal(chapterNumber, chapterTitle, generatedChapter?.id);
  };

  return (
    <div
      key={renderKey}
      className="bg-white flex flex-col justify-between shadow-md rounded overflow-hidden"
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="w-full sm:w-4/12 py-3.5 pl-4 text-left text-xs sm:text-sm font-nunito font-semibold text-slate-50 sm:pl-6"
              >
                {t("chapter.chapters").toUpperCase()}
              </th>
              <th
                scope="col"
                className="hidden sm:table-cell w-5/12 px-3 py-3.5 text-left text-sm font-nunito font-semibold text-slate-50"
              >
                {t("chapter.progression").toUpperCase()}
              </th>
              <th
                scope="col"
                className="hidden md:table-cell w-1/12 px-3 py-3.5 text-left text-sm font-nunito font-semibold text-slate-50"
              >
                {t("chapter.status").toUpperCase()}
              </th>
              <th
                scope="col"
                className="w-auto sm:w-2/12 relative py-3.5 pl-3 pr-4 sm:pr-6"
              >
                <span className="sr-only">{t("dashboard.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {chapterData.map(({ chapter, percentage, completed, on }) => (
              <tr key={chapter.id}>
                <td className="py-4 pl-4 pr-3 text-xs sm:text-sm font-nunito font-normal text-slate-700 sm:pl-6">
                  <div className="block sm:hidden">
                    <div className="flex flex-col space-y-2">
                      <span className="font-medium">
                        {chapter.content[local]}
                      </span>
                      <div className="text-xs text-slate-500">
                        {t("dashboard.questions")} : {completed}/{on}
                      </div>
                      <div className="w-full">
                        <ProgressBar percentage={percentage} />
                      </div>
                      <div className="md:hidden">
                        <ChapterStatusBadge percentage={percentage} />
                      </div>
                    </div>
                  </div>
                  <div className="hidden sm:block">
                    {chapter.content[local]}
                  </div>
                </td>
                <td className="hidden sm:table-cell text-sm py-4 pl-4 pr-3 font-nunito font-normal text-slate-700 sm:pl-6">
                  <div className="flex flex-row items-center gap-x-3">
                    <div className="w-1/3">
                      {t("dashboard.questions")} : {completed}/{on}
                    </div>
                    <ProgressBar percentage={percentage} />
                  </div>
                </td>
                <td className="hidden md:table-cell px-3 py-4 text-sm text-slate-500">
                  <ChapterStatusBadge percentage={percentage} />
                </td>
                <td className="relative py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                  <ChapterTableActionsButton
                    percentage={percentage}
                    chapter={chapter}
                    onAnecdotesClick={() => handleOpenAnecdotesModal(chapter.number, chapter.content[local])}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
